import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import GoogleIntegrations from "@/pages/admin/GoogleIntegrations";
import MediaPicker from "@/pages/admin/MediaPicker";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Activity, RefreshCw, ShieldCheck, Link2, ImageIcon, Search, Gauge, CheckCircle2, EyeOff, RotateCcw, ClipboardCheck, ChevronDown, CircleAlert, Plug, UserRound, Wrench, ArrowUpRight } from "lucide-react";

const CAT_META = {
  seo: { label: "SEO", icon: Search },
  links: { label: "Links", icon: Link2 },
  images: { label: "Images", icon: ImageIcon },
  security: { label: "Security", icon: ShieldCheck },
  uptime: { label: "Uptime", icon: Activity },
};
const SEV_STYLE = {
  critical: "bg-red-100 text-red-700 border-red-200",
  warning: "bg-amber-100 text-amber-700 border-amber-200",
  info: "bg-sky-100 text-sky-700 border-sky-200",
};

const scoreColor = (v) => (v >= 90 ? "text-emerald-600" : v >= 70 ? "text-amber-600" : "text-red-600");
const ringColor = (v) => (v >= 90 ? "#059669" : v >= 70 ? "#d97706" : "#dc2626");

const CL_STATUS = {
  pass: { label: "Pass", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  issues: { label: "Issues", cls: "bg-red-100 text-red-700", icon: CircleAlert },
  attention: { label: "Improve", cls: "bg-amber-100 text-amber-700", icon: CircleAlert },
  integration: { label: "Needs Google setup", cls: "bg-sky-100 text-sky-700", icon: Plug },
  manual: { label: "Human review", cls: "bg-zinc-100 text-zinc-600", icon: UserRound },
};

function ChecklistCategory({ cat }) {
  const [open, setOpen] = useState(false);
  const counts = cat.items.reduce((a, i) => { a[i.status] = (a[i.status] || 0) + 1; return a; }, {});
  return (
    <div className="border-b last:border-0">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 text-left" data-testid={`checklist-cat-${cat.id}`}>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
        <span className="text-sm font-medium flex-1">{cat.label}</span>
        <span className="flex gap-1.5">
          {counts.issues > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700">{counts.issues} issues</span>}
          {counts.attention > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{counts.attention} improve</span>}
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{counts.pass || 0}/{cat.items.length} pass</span>
        </span>
      </button>
      {open && (
        <div className="pb-2">
          {cat.items.map((it) => {
            const m = CL_STATUS[it.status] || CL_STATUS.manual;
            const Icon = m.icon;
            return (
              <div key={it.label} className="flex items-start gap-3 px-11 py-2" data-testid={`checklist-item-${cat.id}-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full shrink-0 mt-0.5 ${m.cls}`}>
                  <Icon className="w-3 h-3" /> {m.label}{it.count > 0 ? ` (${it.count})` : ""}
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-medium">{it.label}</div>
                  <p className="text-xs text-zinc-500 mt-0.5">{it.detail}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CharCounter({ value, range }) {
  const len = (value || "").length;
  const ok = len >= range[0] && len <= range[1];
  return <span className={`text-[10px] tabular-nums ${ok ? "text-emerald-600" : "text-amber-600"}`}>{len} chars (aim {range[0]}–{range[1]})</span>;
}

function FixPanel({ issue, onFixed, onClose, onOpenEditor }) {
  const [info, setInfo] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [pickerField, setPickerField] = useState(null);

  useEffect(() => {
    api.get(`/admin/health/issues/${issue.id}/fix`).then(({ data }) => {
      setInfo(data);
      const v = {};
      (data.fields || []).forEach((f) => { v[f.name] = f.value ?? ""; });
      setValues(v);
    }).catch(() => { toast.error("Could not load fix details"); onClose(); });
  }, [issue.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/health/issues/${issue.id}/fix`, { values });
      toast.success("Fixed — issue marked resolved");
      onFixed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fix failed");
    }
    setSaving(false);
  };

  if (!info) return <div className="px-11 pb-3 text-xs text-zinc-400">Loading fix…</div>;
  if (!info.fixable) {
    return (
      <div className="px-11 pb-3 flex items-center gap-3">
        <p className="text-xs text-zinc-500">This one needs the full editor.</p>
        {info.editor_link && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onOpenEditor(info.editor_link)} data-testid={`fix-open-editor-${issue.id}`}>
            <ArrowUpRight className="h-3.5 w-3.5 mr-1" /> Open in editor
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="mx-11 mb-3 rounded-lg border border-zinc-200 bg-zinc-50/70 p-3 space-y-3" data-testid={`fix-panel-${issue.id}`}>
      {info.note && <p className="text-xs text-zinc-500">{info.note}</p>}
      {info.fields.map((f) => (
        <div key={f.name} className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <label className="text-xs font-medium text-zinc-700">{f.label}</label>
            {f.counter && <CharCounter value={values[f.name]} range={f.counter} />}
          </div>
          {f.type === "textarea" ? (
            <Textarea rows={2} value={values[f.name] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
              className="bg-white text-sm" data-testid={`fix-field-${f.name}`} />
          ) : f.type === "image" ? (
            <div className="flex items-center gap-2">
              {values[f.name] && <img src={values[f.name]} alt="" className="h-10 w-14 object-cover rounded border shrink-0" />}
              <Input value={values[f.name] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                placeholder="Paste an image URL…" className="bg-white text-sm" data-testid={`fix-field-${f.name}`} />
              <Button size="sm" variant="outline" className="h-9 shrink-0" onClick={() => setPickerField(f.name)} data-testid={`fix-pick-image-${issue.id}`}>Library</Button>
            </div>
          ) : (
            <Input type={f.type === "number" ? "number" : "text"} step="any" value={values[f.name] ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
              className="bg-white text-sm" data-testid={`fix-field-${f.name}`} />
          )}
          {f.hint && <p className="text-[11px] text-zinc-400">{f.hint}</p>}
        </div>
      ))}
      <div className="flex items-center gap-2 pt-1">
        <Button size="sm" className="h-8" onClick={save} disabled={saving} data-testid={`fix-save-${issue.id}`}>
          {saving ? "Saving…" : "Save & resolve"}
        </Button>
        <Button size="sm" variant="ghost" className="h-8" onClick={onClose} data-testid={`fix-cancel-${issue.id}`}>Cancel</Button>
        {info.editor_link && (
          <button onClick={() => onOpenEditor(info.editor_link)} className="ml-auto text-[11px] text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1" data-testid={`fix-editor-link-${issue.id}`}>
            Open in full editor <ArrowUpRight className="h-3 w-3" />
          </button>
        )}
      </div>
      <MediaPicker open={!!pickerField} onClose={() => setPickerField(null)}
        onSelect={(url) => setValues((v) => ({ ...v, [pickerField]: url }))} />
    </div>
  );
}

function ScoreRing({ value, label, size = 84 }) {
  const r = (size - 10) / 2, c = 2 * Math.PI * r;
  const v = value ?? 0;
  return (
    <div className="flex flex-col items-center gap-1" data-testid={`health-score-${label.toLowerCase()}`}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth="7" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={ringColor(v)} strokeWidth="7"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - (c * v) / 100}
          style={{ transition: "stroke-dashoffset .8s ease" }} />
      </svg>
      <div className={`-mt-[58px] mb-[26px] text-lg font-semibold ${scoreColor(v)}`}>{value ?? "—"}</div>
      <div className="text-xs text-zinc-500 font-medium">{label}</div>
    </div>
  );
}

function UptimeStrip({ checks }) {
  const fe = checks.filter((c) => c.target === "frontend").slice(-48);
  if (!fe.length) return <p className="text-xs text-zinc-400">No uptime checks yet — the monitor runs every 15 minutes.</p>;
  return (
    <div className="flex items-end gap-[3px]" data-testid="uptime-strip">
      {fe.map((c, i) => (
        <div key={i} title={`${new Date(c.ts).toLocaleTimeString()} · ${c.ok ? "OK" : "DOWN"} · ${c.latency_ms}ms`}
          className={`w-2 rounded-sm ${c.ok ? "bg-emerald-500" : "bg-red-500"}`}
          style={{ height: `${Math.min(34, Math.max(10, (c.latency_ms || 0) / 60))}px` }} />
      ))}
    </div>
  );
}

export default function Health() {
  const [overview, setOverview] = useState(null);
  const [issues, setIssues] = useState([]);
  const [uptime, setUptime] = useState({ checks: [], uptime_pct: null });
  const [checklist, setChecklist] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [filterCat, setFilterCat] = useState("");
  const [filterStatus, setFilterStatus] = useState("open");
  const [fixOpen, setFixOpen] = useState(null);
  const navigate = useNavigate();
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    const [o, u, cl] = await Promise.all([
      api.get("/admin/health/overview"),
      api.get("/admin/health/uptime?hours=24"),
      api.get("/admin/health/checklist"),
    ]);
    setOverview(o.data);
    setUptime(u.data);
    setChecklist(cl.data);
  }, []);

  const loadIssues = useCallback(async () => {
    const params = new URLSearchParams({ status: filterStatus });
    if (filterCat) params.set("category", filterCat);
    const r = await api.get(`/admin/health/issues?${params}`);
    setIssues(r.data);
  }, [filterCat, filterStatus]);

  useEffect(() => { load().catch(() => toast.error("Could not load health data")); }, [load]);
  useEffect(() => { loadIssues().catch(() => {}); }, [loadIssues]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await api.post("/admin/health/scan");
      const id = r.data.scan_id;
      pollRef.current = setInterval(async () => {
        const s = await api.get(`/admin/health/scan/${id}`);
        if (s.data.status !== "running") {
          clearInterval(pollRef.current);
          setScanning(false);
          if (s.data.status === "done") {
            toast.success(`Scan complete — ${s.data.summary?.issues_found ?? 0} findings across ${s.data.summary?.pages_scanned ?? 0} pages`);
            load(); loadIssues();
          } else toast.error("Scan failed — check backend logs");
        }
      }, 2000);
    } catch {
      setScanning(false);
      toast.error("Could not start scan");
    }
  };

  const setIssueStatus = async (id, status) => {
    try {
      await api.patch(`/admin/health/issues/${id}`, { status });
      toast.success(status === "ignored" ? "Issue ignored" : status === "resolved" ? "Marked resolved" : "Reopened");
      loadIssues(); load();
    } catch { toast.error("Update failed"); }
  };

  const openEditor = async (issue) => {
    try {
      const { data } = await api.get(`/admin/health/issues/${issue.id}/fix`);
      if (data.editor_link) navigate(data.editor_link);
      else toast.info("No direct editor for this issue — see the detail for guidance.");
    } catch { toast.error("Could not open the editor"); }
  };

  const scores = overview?.last_scan?.scores;
  const sev = overview?.open_issues?.by_severity || {};

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="health-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Gauge className="h-6 w-6" /> Website Health</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {overview?.last_scan ? `Last scan ${new Date(overview.last_scan.finished_at).toLocaleString()}` : "No scan run yet — run your first scan."}
          </p>
        </div>
        <Button onClick={runScan} disabled={scanning} data-testid="run-scan-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${scanning ? "animate-spin" : ""}`} />
          {scanning ? "Scanning…" : "Run scan"}
        </Button>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-4 bg-white border rounded-xl p-5 mb-6" data-testid="health-scores">
        <ScoreRing value={scores?.overall} label="Overall" />
        <ScoreRing value={scores?.seo} label="SEO" />
        <ScoreRing value={scores?.links} label="Links" />
        <ScoreRing value={scores?.images} label="Images" />
        <ScoreRing value={scores?.security} label="Security" />
        <ScoreRing value={scores?.uptime ?? uptime.uptime_pct} label="Uptime" />
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border rounded-xl p-4">
          <div className="text-xs font-medium text-zinc-500 mb-2">Open issues</div>
          <div className="flex gap-2 flex-wrap" data-testid="open-issue-counts">
            <Badge variant="outline" className={SEV_STYLE.critical}>{sev.critical ?? 0} critical</Badge>
            <Badge variant="outline" className={SEV_STYLE.warning}>{sev.warning ?? 0} warnings</Badge>
            <Badge variant="outline" className={SEV_STYLE.info}>{sev.info ?? 0} info</Badge>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-4 md:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-medium text-zinc-500">Uptime — last 24h (checked every 15 min)</div>
            <div className="text-sm font-semibold" data-testid="uptime-pct">{uptime.uptime_pct != null ? `${uptime.uptime_pct}%` : "—"}</div>
          </div>
          <UptimeStrip checks={uptime.checks} />
        </div>
      </div>

      <div className="bg-white border rounded-xl">
        <div className="flex flex-wrap items-center gap-2 p-4 border-b">
          <span className="text-sm font-medium mr-2">Issues</span>
          <button onClick={() => setFilterCat("")} data-testid="filter-cat-all"
            className={`text-xs px-3 py-1 rounded-full border ${!filterCat ? "bg-zinc-900 text-white" : "hover:bg-zinc-50"}`}>All</button>
          {Object.entries(CAT_META).map(([k, m]) => (
            <button key={k} onClick={() => setFilterCat(k)} data-testid={`filter-cat-${k}`}
              className={`text-xs px-3 py-1 rounded-full border ${filterCat === k ? "bg-zinc-900 text-white" : "hover:bg-zinc-50"}`}>{m.label}</button>
          ))}
          <div className="ml-auto flex gap-1">
            {["open", "resolved", "ignored", "all"].map((s) => (
              <button key={s} onClick={() => setFilterStatus(s)} data-testid={`filter-status-${s}`}
                className={`text-xs px-2.5 py-1 rounded-md ${filterStatus === s ? "bg-zinc-100 font-medium" : "text-zinc-500 hover:bg-zinc-50"}`}>
                {s[0].toUpperCase() + s.slice(1)}</button>
            ))}
          </div>
        </div>
        <div data-testid="issues-list">
          {issues.length === 0 && (
            <div className="p-8 text-center text-sm text-zinc-400">
              {filterStatus === "open" ? "No open issues here — looking healthy." : "Nothing in this view."}
            </div>
          )}
          {issues.map((i) => {
            const Icon = CAT_META[i.category]?.icon || Activity;
            return (
              <div key={i.id} className="border-b last:border-0">
                <div className="flex items-start gap-3 px-4 py-3 hover:bg-zinc-50/60" data-testid={`issue-row-${i.id}`}>
                  <Icon className="h-4 w-4 mt-0.5 text-zinc-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">{i.title}</span>
                      <Badge variant="outline" className={`${SEV_STYLE[i.severity]} text-[10px]`}>{i.severity}</Badge>
                      <span className="text-[11px] text-zinc-400 truncate">{i.page}</span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-0.5">{i.detail}</p>
                  </div>
                  <div className="flex gap-1 shrink-0 items-center">
                    {i.status === "open" && i.fix_mode === "inline" && (
                      <Button size="sm" variant={fixOpen === i.id ? "secondary" : "outline"} className="h-7 px-2 text-xs"
                        onClick={() => setFixOpen(fixOpen === i.id ? null : i.id)} data-testid={`fix-issue-${i.id}`}>
                        <Wrench className="h-3.5 w-3.5 mr-1" /> Fix now
                      </Button>
                    )}
                    {i.status === "open" && i.fix_mode === "editor" && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                        onClick={() => openEditor(i)} data-testid={`fix-goto-${i.id}`}>
                        <ArrowUpRight className="h-3.5 w-3.5 mr-1" /> Open in editor
                      </Button>
                    )}
                    {i.status === "open" ? (
                      <>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-700" title="Mark resolved"
                          onClick={() => setIssueStatus(i.id, "resolved")} data-testid={`resolve-issue-${i.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-zinc-500" title="Ignore"
                          onClick={() => setIssueStatus(i.id, "ignored")} data-testid={`ignore-issue-${i.id}`}>
                          <EyeOff className="h-3.5 w-3.5" /></Button>
                      </>
                    ) : (
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-zinc-500" title="Reopen"
                        onClick={() => setIssueStatus(i.id, "open")} data-testid={`reopen-issue-${i.id}`}>
                        <RotateCcw className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                </div>
                {fixOpen === i.id && (
                  <FixPanel issue={i} onClose={() => setFixOpen(null)} onOpenEditor={(link) => navigate(link)}
                    onFixed={() => { setFixOpen(null); loadIssues(); load(); }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {checklist && (
        <div className="bg-white border rounded-xl mt-6" data-testid="seo-checklist">
          <div className="flex flex-wrap items-center gap-3 p-4 border-b">
            <ClipboardCheck className="w-5 h-5 text-zinc-500" />
            <div className="flex-1 min-w-[200px]">
              <div className="text-sm font-medium">SEO Expert Checklist</div>
              <div className="text-xs text-zinc-500">Full audit across 8 categories — automated where possible, refreshed on every scan.</div>
            </div>
            <div className="flex gap-1.5 flex-wrap" data-testid="checklist-summary">
              <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">{checklist.summary.pass} pass</span>
              <span className="text-[10px] px-2 py-1 rounded-full bg-red-100 text-red-700">{checklist.summary.issues} issues</span>
              <span className="text-[10px] px-2 py-1 rounded-full bg-amber-100 text-amber-700">{checklist.summary.attention} improve</span>
              <span className="text-[10px] px-2 py-1 rounded-full bg-sky-100 text-sky-700">{checklist.summary.integration} need Google setup</span>
              <span className="text-[10px] px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">{checklist.summary.manual} human review</span>
            </div>
          </div>
          {checklist.categories.map((cat) => <ChecklistCategory key={cat.id} cat={cat} />)}
        </div>
      )}

      <GoogleIntegrations />
    </div>
  );
}
